/**
 * ViewerCanvas - Unified Rendering System
 *
 * Updated to use the shared rendering system for consistent visual output
 * with the editor while maintaining viewer-specific UX patterns.
 *
 * Phase 2 Implementation - Canvas Integration
 */

import React, { useCallback, useRef, useEffect, useState } from "react";
import { Position } from "@/types";
import { cn } from "@/lib/utils";
import "@/styles/viewer.css";
import {
  ResponsiveAdaptationFramework,
  useResponsiveAdaptation,
  useTouchDevice,
} from "@/components/mobile/ResponsiveAdaptationFramework";
import { hapticFeedbackService } from "@/services/mobile/HapticFeedbackService";
import {
  useViewerStore,
  useVisibleComponents,
} from "@/stores/viewer/viewer-store";
import { ViewerPerformanceOptimizer } from "./ViewerPerformanceOptimizer";
import {
  ViewerCanvasWrapper,
  ViewerFooter,
} from "@/components/shared/rendering/ViewerCanvasWrapper";
import {
  useSpatialIndexing,
  CANVAS_PERFORMANCE_CONFIG,
} from "@/components/shared/rendering/CanvasCoreUtils";
import {
  NavigationConfig,
  SensorControlConfig,
  SensorResponse,
  CollaborationConfig,
  CollaborationResponse,
} from "@/components/shared/rendering/InteractionContext";
import { PaletteScope } from "@/components/editor/PaletteScope";
import { logger } from "@/utils/logger";
import { normalizeLegacyCanvasBackgroundColor } from "@/utils/theme-aware-legacy-colors";

interface ViewerCanvasProps {
  className?: string;
  projectId: string;
  pageId?: string;
  // Future interaction handlers
  enableInteractions?: boolean;
  onNavigate?: (config: NavigationConfig) => Promise<boolean>;
  onNavigateProject?: (config: NavigationConfig) => Promise<boolean>;
  onApiCall?: (endpoint: string, data: unknown) => Promise<unknown>;
  onSensorControl?: (config: SensorControlConfig) => Promise<SensorResponse>;
  onCollaboration?: (config: CollaborationConfig) => Promise<CollaborationResponse>;
}

interface TouchGestureState {
  isActive: boolean;
  startPosition: Position;
  currentPosition: Position;
  gestureType: "pan" | "scroll" | "none";
  touches: React.Touch[];
}

/**
 * Touch-optimized container for mobile viewing
 */
const TouchOptimizedContainer: React.FC<{
  children: React.ReactNode;
  onScroll?: (position: Position) => void;
}> = ({ children, onScroll }) => {
  const isTouchDevice = useTouchDevice();
  const [touchGesture, setTouchGesture] = useState<TouchGestureState>({
    isActive: false,
    startPosition: { x: 0, y: 0, z: 0 },
    currentPosition: { x: 0, y: 0, z: 0 },
    gestureType: "none",
    touches: [],
  });

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touches = Array.from(e.touches);
    if (touches.length === 1) {
      const touch = touches[0];
      setTouchGesture({
        isActive: true,
        startPosition: { x: touch.clientX, y: touch.clientY, z: 0 },
        currentPosition: { x: touch.clientX, y: touch.clientY, z: 0 },
        gestureType: "scroll",
        touches,
      });
      hapticFeedbackService.trigger("gesture-start");
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!touchGesture.isActive) return;

      const touches = Array.from(e.touches);
      if (touches.length === 1) {
        const touch = touches[0];
        const currentPosition = { x: touch.clientX, y: touch.clientY, z: 0 };

        setTouchGesture((prev) => ({
          ...prev,
          currentPosition,
        }));

        // Calculate scroll delta
        const deltaX = currentPosition.x - touchGesture.startPosition.x;
        const deltaY = currentPosition.y - touchGesture.startPosition.y;

        onScroll?.({ x: deltaX, y: deltaY, z: 0 });
      }
    },
    [touchGesture, onScroll]
  );

  const handleTouchEnd = useCallback(() => {
    setTouchGesture({
      isActive: false,
      startPosition: { x: 0, y: 0, z: 0 },
      currentPosition: { x: 0, y: 0, z: 0 },
      gestureType: "none",
      touches: [],
    });
  }, []);

  if (!isTouchDevice) {
    return <>{children}</>;
  }

  return (
    <div
      className="touch-optimized-container w-full h-full min-w-0"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: "pan-y", width: "100%", minWidth: 0 }}
    >
      {children}
    </div>
  );
};

const ViewerCanvasContent: React.FC<ViewerCanvasProps> = ({
  className,
  projectId,
  pageId,
  enableInteractions = false,
  onNavigate,
  onNavigateProject,
  onApiCall,
  onSensorControl,
  onCollaboration,
}) => {
  const {
    components,
    canvasSettings,
    isLoading,
    error,
    currentPageId,
    allowInteraction,
    enableVirtualization,
    setVisibleBounds,
  } = useViewerStore();

  const canvasRef = useRef<HTMLDivElement>(null);
  const { viewportInfo } = useResponsiveAdaptation();
  const isTouchDevice = useTouchDevice();
  const { isFullscreen } = useViewerStore();

  // Use responsive viewport based on actual window size instead of hardcoded desktop
  const viewport = viewportInfo.viewport;

  // Debug scroll container in fullscreen
  useEffect(() => {
    if (canvasRef.current && isFullscreen) {
      const container = canvasRef.current;
      logger.debug("Viewer fullscreen scroll container", {
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight,
        canScroll: container.scrollHeight > container.clientHeight,
        overflow: window.getComputedStyle(container).overflow,
        overflowY: window.getComputedStyle(container).overflowY,
        height: window.getComputedStyle(container).height,
        maxHeight: window.getComputedStyle(container).maxHeight,
      });
    }
  }, [isFullscreen, components.length]);

  // Note: Project loading is handled by ViewerPage.tsx to prevent duplicate calls

  // Use optimized visible components hook
  const visibleComponents = useVisibleComponents();

  // Enable spatial indexing for performance (even in viewer mode)
  const enableSpatialIndexing = components.length > CANVAS_PERFORMANCE_CONFIG.VIRTUALIZATION_THRESHOLD;
  const spatialIndex = useSpatialIndexing(
    components,
    viewport,
    enableSpatialIndexing
  );

  // Handle scroll for viewport culling
  const handleScroll = useCallback((position: Position) => {
    if (!enableVirtualization) return;

    const container = canvasRef.current;
    if (!container) return;

    const bounds = {
      x: container.scrollLeft + position.x,
      y: container.scrollTop + position.y,
      width: container.clientWidth,
      height: container.clientHeight,
    };

    setVisibleBounds(bounds);
  }, [enableVirtualization, setVisibleBounds]);

  // Debug logging (only for errors)
  useEffect(() => {
    if (components.length === 0 && !isLoading) {
      logger.debug("Viewer canvas has no components", {
        projectId,
        pageId,
        currentPageId,
      });
    }
  }, [
    projectId,
    pageId,
    currentPageId,
    components.length,
    isLoading,
    visibleComponents.length,
    enableSpatialIndexing,
    enableVirtualization,
    enableInteractions,
    allowInteraction,
  ]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <div className="text-muted-foreground">Loading project...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-destructive">
          <div className="text-lg font-medium mb-2">Error loading project</div>
          <div className="text-sm">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <ViewerPerformanceOptimizer>
      {/* Container wrapper to match editor layout */}
      <div
        className={cn(
          "viewer-canvas-container",
          "w-full h-full",
          "flex flex-col",
          className,
        )}
      >
        {/* Canvas viewport container - full width without padding for viewer */}
        <div
          ref={canvasRef}
          className={cn(
            "viewer-canvas-unified relative",
            "flex-1",
            "overflow-y-auto overflow-x-hidden", // Prevent horizontal scroll, allow vertical
            "focus:outline-none",
            isTouchDevice && "touch-pan-y touch-pinch-zoom",
          )}
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            backgroundColor: normalizeLegacyCanvasBackgroundColor(
              canvasSettings?.background?.color || '#f9fafb',
            ),
          }}
        >
          <TouchOptimizedContainer onScroll={handleScroll}>
            <PaletteScope
              paletteParams={{ projectId }}
              className="viewer-content w-full h-full"
            >
              <ViewerCanvasWrapper
                components={components} // Use all components for consistent positioning with editor
                canvasSettings={canvasSettings}
                viewport={viewport}
                enableInteractions={enableInteractions}
                onNavigate={onNavigate}
                onNavigateProject={onNavigateProject}
                onApiCall={onApiCall}
                onSensorControl={onSensorControl}
                onCollaboration={onCollaboration}
                spatialIndex={spatialIndex ?? undefined}
                className="h-full"
              />
            </PaletteScope>
        </TouchOptimizedContainer>
        </div>

        {/* Viewer footer */}
        <ViewerFooter />
      </div>
    </ViewerPerformanceOptimizer>
  );
};

export const ViewerCanvas: React.FC<ViewerCanvasProps> = (props) => (
  <ResponsiveAdaptationFramework>
    <ViewerCanvasContent {...props} />
  </ResponsiveAdaptationFramework>
);
